/**
 * Financial Analytics & Metrics Utilities
 * Calculate derived metrics from transactions for insights and reporting
 * 
 * Metrics:
 * - Total Income/Expenses
 * - Net Balance
 * - Savings Rate
 * - Top Spending Categories
 * - Budget vs Actual
 * - Disposable Income
 * - Investment Ratio
 * - Recurring Expense Tracking
 */

import { CATEGORY_TYPES } from '@/constants/categoryTaxonomy';
import { startOfWeek, startOfMonth, format } from 'date-fns';
// CATEGORY CORE SSOT: Use single source of truth for category names
import { getCategoryDisplayName, normalizeToCategoryTaxonomyId } from '@/constants/categoryCore';

/**
 * Get proper display name for a category from transaction data
 * Uses categoryCore SSOT for consistent naming
 */
function getDisplayCategoryName(transaction) {
  // Try categoryId first (most reliable)
  if (transaction.categoryId) {
    const canonicalId = normalizeToCategoryTaxonomyId(transaction.categoryId);
    if (canonicalId) {
      const name = getCategoryDisplayName(canonicalId);
      if (name && name !== 'Unknown') return name;
    }
    // Try direct lookup
    const directName = getCategoryDisplayName(transaction.categoryId);
    if (directName && directName !== 'Unknown') return directName;
  }
  
  // Try categoryName if present
  if (transaction.categoryName) {
    const canonicalId = normalizeToCategoryTaxonomyId(transaction.categoryName);
    if (canonicalId) {
      const name = getCategoryDisplayName(canonicalId);
      if (name && name !== 'Unknown') return name;
    }
    // If it's already a nice name, return it
    if (!transaction.categoryName.includes('-') && !transaction.categoryName.includes('_')) {
      return transaction.categoryName;
    }
  }
  
  // Try category field
  if (transaction.category) {
    const canonicalId = normalizeToCategoryTaxonomyId(transaction.category);
    if (canonicalId) {
      const name = getCategoryDisplayName(canonicalId);
      if (name && name !== 'Unknown') return name;
    }
    return transaction.category;
  }
  
  return 'Uncategorized';
}

/**
 * Calculate total income
 * @param {Array} transactions - All transactions
 * @param {Object} dateRange - Optional {start: Date, end: Date}
 * @returns {Number}
 */
export function calculateTotalIncome(transactions, dateRange = null) {
  return filterByDateRange(transactions, dateRange)
    .filter(t => t.type === CATEGORY_TYPES.INCOME)
    .reduce((total, t) => total + (t.amount || 0), 0);
}

/**
 * Calculate total expenses
 * @param {Array} transactions 
 * @param {Object} dateRange 
 * @returns {Number}
 */
export function calculateTotalExpenses(transactions, dateRange = null) {
  return filterByDateRange(transactions, dateRange)
    .filter(t => t.type === CATEGORY_TYPES.EXPENSE)
    .reduce((total, t) => total + (t.amount || 0), 0);
}

/**
 * Calculate net balance
 * @param {Array} transactions 
 * @param {Object} dateRange 
 * @returns {Number}
 */
export function calculateNetBalance(transactions, dateRange = null) {
  const income = calculateTotalIncome(transactions, dateRange);
  const expenses = calculateTotalExpenses(transactions, dateRange);
  return income - expenses;
}

/**
 * Calculate savings rate (%)
 * @param {Array} transactions 
 * @param {Object} dateRange 
 * @returns {Number}
 */
export function calculateSavingsRate(transactions, dateRange = null) {
  const income = calculateTotalIncome(transactions, dateRange);
  const expenses = calculateTotalExpenses(transactions, dateRange);
  
  if (income === 0) return 0;
  
  const savings = income - expenses;
  return ((savings / income) * 100).toFixed(2);
}

/**
 * Get top spending categories
 * @param {Array} transactions 
 * @param {Number} limit 
 * @param {Object} dateRange 
 * @returns {Array}
 */
export function getTopSpendingCategories(transactions, limit = 5, dateRange = null) {
  const filtered = filterByDateRange(transactions, dateRange)
    .filter(t => t.type === CATEGORY_TYPES.EXPENSE);

  // Group by category - use getDisplayCategoryName for consistent naming
  const categoryTotals = filtered.reduce((acc, t) => {
    const category = getDisplayCategoryName(t);
    if (!acc[category]) {
      acc[category] = {
        category,
        total: 0,
        count: 0,
        transactions: []
      };
    }
    acc[category].total += t.amount || 0;
    acc[category].count += 1;
    acc[category].transactions.push(t);
    return acc;
  }, {});

  // Convert to array and sort
  return Object.values(categoryTotals)
    .sort((a, b) => b.total - a.total)
    .slice(0, limit)
    .map(cat => ({
      ...cat,
      percentage: filtered.length > 0 
        ? ((cat.total / calculateTotalExpenses(filtered)) * 100).toFixed(2)
        : 0
    }));
}

/**
 * Calculate budget vs actual
 * @param {Array} transactions 
 * @param {Object} budgets - { categoryId: budgetAmount }
 * @param {Object} dateRange 
 * @returns {Array}
 */
export function calculateBudgetVsActual(transactions, budgets, dateRange = null) {
  const filtered = filterByDateRange(transactions, dateRange)
    .filter(t => t.type === CATEGORY_TYPES.EXPENSE);

  // Group spending by category - use getDisplayCategoryName for consistent naming
  const categorySpending = filtered.reduce((acc, t) => {
    const category = getDisplayCategoryName(t);
    acc[category] = (acc[category] || 0) + (t.amount || 0);
    return acc;
  }, {});

  // Compare with budgets
  return Object.entries(budgets).map(([category, budgetAmount]) => {
    const actual = categorySpending[category] || 0;
    const difference = budgetAmount - actual;
    const percentage = budgetAmount > 0 
      ? ((actual / budgetAmount) * 100).toFixed(2)
      : 0;

    return {
      category,
      budget: budgetAmount,
      actual,
      difference,
      percentage: parseFloat(percentage),
      status: getbudgetStatus(percentage),
      isOverBudget: actual > budgetAmount
    };
  }).sort((a, b) => b.actual - a.actual);
}

/**
 * Get budget status
 */
function getbudgetStatus(percentage) {
  if (percentage >= 100) return 'exceeded';
  if (percentage >= 90) return 'critical';
  if (percentage >= 80) return 'warning';
  if (percentage >= 50) return 'good';
  return 'excellent';
}

/**
 * Calculate disposable income
 * @param {Array} transactions 
 * @param {Object} fixedExpenses - { category: amount }
 * @param {Object} dateRange 
 * @returns {Object}
 */
export function calculateDisposableIncome(transactions, fixedExpenses = {}, dateRange = null) {
  const income = calculateTotalIncome(transactions, dateRange);
  const totalExpenses = calculateTotalExpenses(transactions, dateRange);
  
  // Calculate fixed expenses
  const fixed = Object.values(fixedExpenses).reduce((sum, amount) => sum + amount, 0);
  
  // Variable expenses = total expenses - fixed
  const variable = totalExpenses - fixed;
  
  // Disposable = Income - Fixed - Variable
  const disposable = income - totalExpenses;

  return {
    income,
    fixedExpenses: fixed,
    variableExpenses: variable,
    totalExpenses,
    disposableIncome: disposable,
    disposablePercentage: income > 0 ? ((disposable / income) * 100).toFixed(2) : 0
  };
}

/**
 * Calculate investment ratio (%)
 * @param {Array} transactions 
 * @param {Object} dateRange 
 * @returns {Number}
 */
export function calculateInvestmentRatio(transactions, dateRange = null) {
  const income = calculateTotalIncome(transactions, dateRange);
  
  const investments = filterByDateRange(transactions, dateRange)
    .filter(t => 
      t.type === CATEGORY_TYPES.EXPENSE && 
      (t.category === 'Investments' || t.category?.toLowerCase().includes('investment'))
    )
    .reduce((total, t) => total + (t.amount || 0), 0);

  if (income === 0) return 0;
  
  return ((investments / income) * 100).toFixed(2);
}

/**
 * Track recurring expenses
 * @param {Array} transactions 
 * @returns {Array}
 */
export function trackRecurringExpenses(transactions) {
  // Filter expenses with recurring flag or in recurring categories
  const recurringCategories = [
    'Utilities & Bills',
    'Housing & Rent',
    'Insurance & Protection',
    'Debt & Loans'
  ];

  const recurring = transactions
    .filter(t => 
      t.type === CATEGORY_TYPES.EXPENSE &&
      (t.metadata?.recurring || recurringCategories.includes(t.category))
    );

  // Group by description/category for pattern detection
  const grouped = recurring.reduce((acc, t) => {
    const key = `${t.category}-${Math.round(t.amount)}`;
    if (!acc[key]) {
      acc[key] = {
        category: t.category,
        description: t.description,
        amount: t.amount,
        dates: [],
        frequency: 0
      };
    }
    acc[key].dates.push(new Date(t.date));
    acc[key].frequency += 1;
    return acc;
  }, {});

  // Calculate intervals and next due dates
  return Object.values(grouped)
    .filter(item => item.frequency >= 2) // At least 2 occurrences
    .map(item => {
      const sortedDates = item.dates.sort((a, b) => a - b);
      const intervals = [];
      
      for (let i = 1; i < sortedDates.length; i++) {
        const days = Math.round((sortedDates[i] - sortedDates[i-1]) / (1000 * 60 * 60 * 24));
        intervals.push(days);
      }
      
      const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const lastDate = sortedDates[sortedDates.length - 1];
      const nextDue = new Date(lastDate.getTime() + (avgInterval * 24 * 60 * 60 * 1000));
      
      return {
        ...item,
        avgInterval: Math.round(avgInterval),
        lastPaid: lastDate,
        nextDue,
        isPastDue: nextDue < new Date(),
        daysUntilDue: Math.round((nextDue - new Date()) / (1000 * 60 * 60 * 24))
      };
    })
    .sort((a, b) => a.daysUntilDue - b.daysUntilDue);
}

/**
 * Get spending breakdown by category
 * @param {Array} transactions 
 * @param {Object} dateRange 
 * @returns {Array}
 */
export function getSpendingBreakdown(transactions, dateRange = null) {
  const filtered = filterByDateRange(transactions, dateRange)
    .filter(t => t.type === CATEGORY_TYPES.EXPENSE);

  const total = calculateTotalExpenses(filtered);

  const categoryData = filtered.reduce((acc, t) => {
    const category = getDisplayCategoryName(t);
    if (!acc[category]) {
      acc[category] = { amount: 0, count: 0 };
    }
    acc[category].amount += t.amount || 0;
    acc[category].count += 1;
    return acc;
  }, {});

  return Object.entries(categoryData)
    .map(([category, data]) => ({
      category,
      amount: data.amount,
      count: data.count,
      percentage: total > 0 ? ((data.amount / total) * 100).toFixed(2) : 0,
      avgTransaction: (data.amount / data.count).toFixed(2)
    }))
    .sort((a, b) => b.amount - a.amount);
}

/**
 * Get income breakdown by source
 * @param {Array} transactions 
 * @param {Object} dateRange 
 * @returns {Array}
 */
export function getIncomeBreakdown(transactions, dateRange = null) {
  const filtered = filterByDateRange(transactions, dateRange)
    .filter(t => t.type === CATEGORY_TYPES.INCOME);

  const total = calculateTotalIncome(filtered);

  const sourceData = filtered.reduce((acc, t) => {
    const source = getDisplayCategoryName(t);
    if (!acc[source]) {
      acc[source] = { amount: 0, count: 0 };
    }
    acc[source].amount += t.amount || 0;
    acc[source].count += 1;
    return acc;
  }, {});

  return Object.entries(sourceData)
    .map(([source, data]) => ({
      source,
      amount: data.amount,
      count: data.count,
      percentage: total > 0 ? ((data.amount / total) * 100).toFixed(2) : 0,
      avgIncome: (data.amount / data.count).toFixed(2)
    }))
    .sort((a, b) => b.amount - a.amount);
}

/**
 * Calculate monthly summary
 * @param {Array} transactions 
 * @param {Number} month - 0-11
 * @param {Number} year 
 * @returns {Object}
 */
export function calculateMonthlySummary(transactions, month, year) {
  const dateRange = {
    start: new Date(year, month, 1),
    end: new Date(year, month + 1, 0)
  };

  const income = calculateTotalIncome(transactions, dateRange);
  const expenses = calculateTotalExpenses(transactions, dateRange);
  const balance = income - expenses;
  const savingsRate = calculateSavingsRate(transactions, dateRange);
  const topCategories = getTopSpendingCategories(transactions, 5, dateRange);

  return {
    month,
    year,
    period: `${dateRange.start.toLocaleDateString('default', { month: 'long' })} ${year}`,
    income,
    expenses,
    balance,
    savingsRate: parseFloat(savingsRate),
    topCategories,
    transactionCount: filterByDateRange(transactions, dateRange).length
  };
}

/**
 * Compare periods
 * @param {Array} transactions 
 * @param {Object} currentPeriod 
 * @param {Object} previousPeriod 
 * @returns {Object}
 */
export function comparePeriods(transactions, currentPeriod, previousPeriod) {
  const current = {
    income: calculateTotalIncome(transactions, currentPeriod),
    expenses: calculateTotalExpenses(transactions, currentPeriod),
    balance: calculateNetBalance(transactions, currentPeriod)
  };

  const previous = {
    income: calculateTotalIncome(transactions, previousPeriod),
    expenses: calculateTotalExpenses(transactions, previousPeriod),
    balance: calculateNetBalance(transactions, previousPeriod)
  };

  const calculateChange = (curr, prev) => {
    if (prev === 0) return curr > 0 ? 100 : 0;
    return (((curr - prev) / prev) * 100).toFixed(2);
  };

  return {
    current,
    previous,
    changes: {
      income: {
        amount: current.income - previous.income,
        percentage: parseFloat(calculateChange(current.income, previous.income))
      },
      expenses: {
        amount: current.expenses - previous.expenses,
        percentage: parseFloat(calculateChange(current.expenses, previous.expenses))
      },
      balance: {
        amount: current.balance - previous.balance,
        percentage: parseFloat(calculateChange(current.balance, previous.balance))
      }
    }
  };
}

/**
 * Aggregate historical spending analytics for dashboard insights
 * @param {Array} transactions
 * @param {Object} options - { weeks: number, months: number }
 * @returns {Object}
 */
export function aggregateHistoricalSpending(transactions, options = {}) {
  const { weeks = 6, months = 6 } = options;
  if (!Array.isArray(transactions) || transactions.length === 0) {
    return {
      weeklyTotals: [],
      monthlyTotals: [],
      topCategories: [],
      averageWeeklySpend: 0,
      averageMonthlySpend: 0,
      monthlyIncomeExpenses: []
    };
  }

  const expenseTransactions = transactions.filter(tx => {
    const type = tx.categoryType || tx.type;
    return type === CATEGORY_TYPES.EXPENSE || type === 'expense';
  });

  const incomeTransactions = transactions.filter(tx => {
    const type = tx.categoryType || tx.type;
    return type === CATEGORY_TYPES.INCOME || type === 'income';
  });

  const weeklyMap = new Map();
  const monthlyMap = new Map();
  const monthlyIncomeMap = new Map();
  const monthlyExpenseMap = new Map();

  expenseTransactions.forEach(tx => {
    const date = tx.date ? new Date(tx.date) : new Date();
    const amount = Math.abs(tx.amount || 0);

    const weekKey = startOfWeek(date, { weekStartsOn: 1 }).toISOString();
    weeklyMap.set(weekKey, (weeklyMap.get(weekKey) || 0) + amount);

    const monthKey = startOfMonth(date).toISOString();
    monthlyMap.set(monthKey, (monthlyMap.get(monthKey) || 0) + amount);
    monthlyExpenseMap.set(monthKey, (monthlyExpenseMap.get(monthKey) || 0) + amount);
  });

  incomeTransactions.forEach(tx => {
    const date = tx.date ? new Date(tx.date) : new Date();
    const amount = Math.abs(tx.amount || 0);

    const monthKey = startOfMonth(date).toISOString();
    monthlyIncomeMap.set(monthKey, (monthlyIncomeMap.get(monthKey) || 0) + amount);
  });

  const weeklyTotals = Array.from(weeklyMap.entries())
    .sort((a, b) => new Date(a[0]) - new Date(b[0]))
    .slice(-weeks)
    .map(([key, total]) => ({
      label: format(new Date(key), "dd MMM"),
      total: Number(total.toFixed(2))
    }));

  const monthlyTotals = Array.from(monthlyMap.entries())
    .sort((a, b) => new Date(a[0]) - new Date(b[0]))
    .slice(-months)
    .map(([key, total]) => ({
      label: format(new Date(key), 'MMM yyyy'),
      total: Number(total.toFixed(2))
    }));

  // Build monthlyIncomeExpenses for insights
  const allMonthKeys = new Set([...monthlyIncomeMap.keys(), ...monthlyExpenseMap.keys()]);
  const monthlyIncomeExpenses = Array.from(allMonthKeys)
    .sort((a, b) => new Date(a) - new Date(b))
    .slice(-months)
    .map(key => {
      const income = Number((monthlyIncomeMap.get(key) || 0).toFixed(2));
      const expenses = Number((monthlyExpenseMap.get(key) || 0).toFixed(2));
      const net = Number((income - expenses).toFixed(2));
      const savingsRate = income > 0 ? Number(((net / income) * 100).toFixed(1)) : null;
      return {
        label: format(new Date(key), 'MMM yyyy'),
        income,
        expenses,
        net,
        savingsRate
      };
    });

  const weeklyAverage = weeklyTotals.length
    ? weeklyTotals.reduce((sum, entry) => sum + entry.total, 0) / weeklyTotals.length
    : 0;

  const monthlyAverage = monthlyTotals.length
    ? monthlyTotals.reduce((sum, entry) => sum + entry.total, 0) / monthlyTotals.length
    : 0;

  const topCategories = getTopSpendingCategories(expenseTransactions, 5);

  return {
    weeklyTotals,
    monthlyTotals,
    topCategories,
    averageWeeklySpend: Number(weeklyAverage.toFixed(2)),
    averageMonthlySpend: Number(monthlyAverage.toFixed(2)),
    monthlyIncomeExpenses
  };
}

/**
 * Helper: Filter transactions by date range
 */
function filterByDateRange(transactions, dateRange) {
  if (!dateRange) return transactions;

  const { start, end } = dateRange;
  return transactions.filter(t => {
    const tDate = new Date(t.date);
    return tDate >= start && tDate <= end;
  });
}

/**
 * Get current month date range
 */
export function getCurrentMonthRange() {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth(), 1),
    end: new Date(now.getFullYear(), now.getMonth() + 1, 0)
  };
}

/**
 * Get last month date range
 */
export function getLastMonthRange() {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
    end: new Date(now.getFullYear(), now.getMonth(), 0)
  };
}

/**
 * Get year-to-date range
 */
export function getYearToDateRange() {
  const now = new Date();
  return {
    start: new Date(now.getFullYear(), 0, 1),
    end: now
  };
}

export default {
  calculateTotalIncome,
  calculateTotalExpenses,
  calculateNetBalance,
  calculateSavingsRate,
  getTopSpendingCategories,
  calculateBudgetVsActual,
  calculateDisposableIncome,
  calculateInvestmentRatio,
  trackRecurringExpenses,
  getSpendingBreakdown,
  getIncomeBreakdown,
  calculateMonthlySummary,
  comparePeriods,
  aggregateHistoricalSpending,
  getCurrentMonthRange,
  getLastMonthRange,
  getYearToDateRange
};
